<template>
  <div class="px-6 w-full">
    <div class="flex w-full justify-between items-center">
      <CommentUserAvatar
        v-if="!comment.is_model_submission"
        :userName="standard_name"
        :userProp="user"
        :timeAgo="comment.time_ago"
        :dateTimeAgo="comment.date_time_ago"
        :isCommentCreatedAfterRoundTableEnd="created_after_end"
        :isCommentMine="
          getMyRTUserDetails.id === comment.user_id &&
          !hasTeacherPermissionsInCourse
        "
      />
      <div v-else>
        <i
          class="fa fa-star text-amber-300 text-lg mr-2.5 ml-1.5"
          aria-hidden="true"
        />
        <span class="text-sm font-bold">Model Submission</span>
      </div>
      <div class="flex items-center">
        <!-- Teacher Flags -->
        <Tooltip
          v-if="
            comment.flagged_by.length &&
            hasTeacherPermissionsInCourse &&
            !comment.is_model_submission
          "
          :text="flag_tooltip_message"
          styleTooltip="top-0 right-full mt-0.5 mr-2 py-1.5"
          backgroundColor="bg-coolGray-800"
          textColor="text-xs text-gray-100"
        >
          <div class="mr-2">
            <button class="w-8 h-8 hover:bg-coolGray-200 rounded-full">
              <i class="fa fa-flag text-red-600" style="font-size: 12px"></i>
            </button>
            <span>{{ comment.flagged_by.length }}</span>
          </div>
        </Tooltip>
        <!-- Student Flags -->
        <button
          v-if="userFlaggedComment"
          class="w-8 h-8 hover:bg-coolGray-200 rounded-full mr-2"
        >
          <i class="fa fa-flag text-red-600" style="font-size: 12px"></i>
        </button>
        <!-- Star Container -->
        <StarButton
          v-if="!comment.is_model_submission"
          class="mr-2"
          type="comment-card"
          :comment="comment"
        />
        <!-- Options Button -->
        <EntryOptions
          v-if="!isReportPage && getMyRTUserDetails.id !== user.id"
          class="-mt-0.5"
          :entry="comment"
          :type="'comment'"
        />
      </div>
    </div>
    <!-- Comment Content -->
    <div
      v-if="comment.content"
      class="relative my-3 pl-10 w-full break-words"
      :id="'comment-card-' + comment.id"
    >
      <div
        v-show="showFullComments(comment.id)"
        class="relative my-1.5 w-full break-words"
      >
        <div
          @click="goToExternalLink"
          v-html="comment.content"
          class="whitespace-pre-wrap relative"
          v-linkified:options="{ className: 'underline' }"
        ></div>
      </div>
      <div
        v-show="!showFullComments(comment.id)"
        class="relative my-1.5 w-full break-words"
      >
        <div
          @click="goToExternalLink"
          v-html="comment.content"
          class="whitespace-pre-wrap relative"
          v-linkified:options="{ className: 'underline' }"
        ></div>
        <div v-show="comment.content.length > 180" class="z-1" />
      </div>
    </div>
  </div>
</template>

<script>
  import Vue from 'vue';
  import { mapGetters } from 'vuex';
  import find from 'lodash/find';
  /* import indexOf from 'lodash/indexOf'; */
  import moment from 'moment';
  import linkify from 'vue-linkify';

  Vue.directive('linkified', linkify);

  export default Vue.extend({
    components: {
      Tooltip: () => import('../../../../components/Tooltip.vue'),
      EntryOptions: () =>
        import(
          'features/onlineRoundtables/components/entryOptions/entryOptions.vue'
        ),
      StarButton: () =>
        import('features/onlineRoundtables/components/starButton/starButton'),
      CommentUserAvatar: () => import('../commentUserAvatar'),
    },
    data() {
      return {
        see_more_comments: [],
        links: [],
      };
    },
    props: ['submissionComment', 'commentReply'],
    computed: {
      ...mapGetters([
        'getCourseDetail',
        'getCurrentRT',
        'getMyRTUserDetails',
        'hasTeacherPermissionsInCourse',
        'getStudentsInCourse',
        'getProfile',
      ]),
      isReportPage() {
        return this.$route.name === 'course.student-summary';
      },
      /* comment_date_time_ago() {
        return this.submission.start_date
          ? moment(this.submission.start_date, moment.ISO_8601).fromNow()
          : null;
      }, */
      comment() {
        if (this.commentReply) {
          const reply = this.commentReply;
          reply.time_ago = moment(reply.created_at).fromNow();
          reply.date_time_ago = moment(
            reply.created_at,
            moment.ISO_8601
          ).format('LLL');
          return this.commentReply;
        }
        if (this.submissionComment) {
          const subComment = this.submissionComment;
          subComment.time_ago = moment(subComment.created_at).fromNow();
          subComment.date_time_ago = moment(
            subComment.created_at,
            moment.ISO_8601
          ).format('LLL');
          return this.submissionComment;
        }
        return {
          time_ago: '----',
          content: '',
          starred_by: [],
          flagged_by: [],
          comment_id: '12345',
          meta: {
            anonymity_details: {},
          },
        };
      },
      show_anonymous_user() {
        const { enable_annonymous, reveal_names } = this.getCurrentRT;
        return (
          enable_annonymous &&
          !reveal_names &&
          !this.is_teacher_comment &&
          !this.isReportPage
        );
      },
      user() {
        const user = this.show_anonymous_user
          ? this.comment.anonymous_identity
          : this.comment.user;
        return (
          user || {
            name: '----',
            avatar_url: '/static/avatars/avatar_1.png',
            id: '12345',
          }
        );
      },
      is_teacher_comment() {
        const teacher_ids = this.getCourseDetail.users
          .filter((user) => {
            return (
              user.subscription.role === 'author' ||
              user.subscription.role === 'co-teacher' ||
              user.subscription.role === 'admin'
            );
          })
          .map((teacher) => teacher.id);
        return teacher_ids.includes(this.comment.user_id);
      },
      created_after_end() {
        const endedTime = this.getCurrentRT.end_date;
        if (endedTime && this.comment.created_at > endedTime) {
          return true;
        }
        return false;
      },
      standard_name() {
        return this.user.preferred_name || this.user.name;
      },
      commentsToShow() {
        return this.see_more_comments;
      },
      /* star_count() {
        return this.comment.starred_by.length;
      }, */
      flaggers_array() {
        // Find the ids of those who flagged the comment and get their names
        return this.comment.flagged_by
          .map((user_id) => {
            const user = find(this.getStudentsInCourse, { id: user_id });
            return user ? user.name : [];
          })
          .join(', ');
      },
      flag_tooltip_message() {
        return `Flagged by: ${this.flaggers_array}`;
      },
      userFlaggedComment() {
        return this.comment.flagged_by.includes(this.getProfile.id);
      },
    },
    methods: {
      /* getRealUser: function(userId) {
        return find(this.getCourseDetail.users, (user) => user.id === userId);
      }, */
      showFullComments: function (id) {
        return Boolean(this.commentsToShow.includes(id));
      },
      goToExternalLink: function (e) {
        if (e.target.tagName === 'A') {
          e.preventDefault();
          window.open(e.srcElement.attributes[0].value, '_blank');
        }
      },
      /* toggleFullComments: function(id) {
        setTimeout(this.linkify);
        if (this.commentsToShow.includes(id)) {
          this.see_more_comments.splice(indexOf(this.see_more_comments, id), 1);
          return;
        }
        this.see_more_comments.push(id);
        return;
      }, */
    },
  });
</script>



// WEBPACK FOOTER //
// commentCard.vue?312609e2